import { observer } from 'mobx-react'
import React from 'react'
import styles from './DocumentVersionsTab.sass'
import Avatar from 'components/Avatar'
import Button from 'components/Button'
import { Dropdown, DropdownTitle, DropdownItem } from 'components/Dropdown'
import FileButton from 'components/FileButton'
import Icon from 'components/Icon'
import { IconButton } from 'components/IconButton'
import { Form, FormRow, FormGroup, TextInputContainer } from 'components/inputs'
import TextInput from 'components/inputs/TextInput'
import {
  ModalSheet,
  ModalSheetHeader,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentTitle,
  ModalSheetContentDescription,
  ModalSheetFooter,
  ModalSheetFooterSpacer,
  ModalSheetFooterButtonGroup,
  ModalSheetSection,
  ModalSheetListHeader,
  ModalSheetListItem,
} from 'components/ModalSheet'
import DocumentVersion from 'documents/DocumentVersion'
import Member from 'iam/models/Member'
import {
  DocumentDetailsModalViewModel,
  NewVersionModalViewModel,
  RevertVersionModalViewModel,
} from '../DocumentDetailsModalViewModel'

interface DocumentVersionsTabProps {
  vm: DocumentDetailsModalViewModel
}

export default observer(function DocumentVersionsTab({
  vm,
}: DocumentVersionsTabProps) {
  return (
    <div className={styles.documentVersions}>
      <div className={styles.documentVersionsSection}>
        <ModalSheetSection bordered>
          <ModalSheetListHeader>
            <div className={styles.documentVersionsHeader}>
              <Dropdown
                autoDismiss
                position="bottom-start"
                button={
                  <Button
                    size="baseline"
                    borderless
                    type="translucent"
                    iconRight={<Icon name="dropdown" />}
                  >
                    {vm.versionSortOrder === 'desc'
                      ? 'Newest to oldest'
                      : 'Oldest to newest'}
                  </Button>
                }
              >
                <DropdownTitle>Sort versions by</DropdownTitle>
                <DropdownItem
                  active={vm.versionSortOrder === 'desc'}
                  checkmark={vm.versionSortOrder === 'desc'}
                  onClick={() => vm.setVersionsSort('desc')}
                >
                  Newest to oldest
                </DropdownItem>
                <DropdownItem
                  active={vm.versionSortOrder === 'asc'}
                  checkmark={vm.versionSortOrder === 'asc'}
                  onClick={() => vm.setVersionsSort('asc')}
                >
                  Oldest to newest
                </DropdownItem>
              </Dropdown>
              {vm.canUploadVersion && !vm.viewAsAdmin && (
                <FileButton
                  onChange={(files: FileList) => vm.uploadVersion(files[0])}
                >
                  <Button borderless size="baseline" type="text">
                    Upload New Version
                  </Button>
                </FileButton>
              )}
            </div>
          </ModalSheetListHeader>
        </ModalSheetSection>
        <div className={styles.sectionList}>
          {vm.sortedDocumentVersions.map((version) => {
            const isLatest = vm.documentVersions[0] === version
            return (
              <ModalSheetListItem key={version.id}>
                <VersionItem version={version} isCurrent={isLatest}>
                  <Dropdown
                    button={
                      <IconButton>
                        <Icon name="more" />
                      </IconButton>
                    }
                    autoDismiss
                    position="bottom-end"
                  >
                    <DropdownTitle>Version Actions</DropdownTitle>
                    <DropdownItem
                      disabled={!version.downloadable}
                      icon={<Icon name="download" />}
                      onClick={() => vm.downloadVersion(version)}
                    >
                      Download File
                    </DropdownItem>
                    {!isLatest && (
                      <DropdownItem
                        onClick={() => vm.revertVersion(version)}
                        disabled={isLatest}
                        icon={<Icon name="add" />}
                      >
                        Revert to this version
                      </DropdownItem>
                    )}
                  </Dropdown>
                </VersionItem>
              </ModalSheetListItem>
            )
          })}
        </div>
      </div>
    </div>
  )
})

const docVersionDateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
})

interface VersionItemProps {
  version: DocumentVersion
  children: React.ReactNode
  isCurrent?: boolean
}

export const VersionItem = observer(function VersionItem({
  version,
  children,
  isCurrent = false,
}: VersionItemProps) {
  return (
    <div className={styles.versionItem}>
      <div className={styles.versionDetails}>
        <div className={styles.versionAuthor}>
          <Avatar subject={version.author || Member.none} size={30} />
        </div>
        <div className={styles.versionInfo}>
          <div className={styles.versionTitle}>
            <span>{version.files[0].fileName}</span>
            {isCurrent && (
              <>
                <span> &middot; </span>
                <span className={styles.versionCurrentText}>
                  Current Version
                </span>
              </>
            )}
          </div>
          <div className={styles.versionSubtitle}>
            {docVersionDateFormatter.format(version.dateCreated)}
          </div>
          <div className={styles.versionSubsubtitle}>{version.message}</div>
        </div>
      </div>
      <div className={styles.versionExtras}>{children}</div>
    </div>
  )
})

interface NewVersionModalProps {
  vm: NewVersionModalViewModel
}

export const NewVersionModal = observer(function NewVersionModal({
  vm,
}: NewVersionModalProps) {
  return (
    <ModalSheet showing={vm.showing} onRequestClose={vm.cancel}>
      <ModalSheetHeader withCloseButton></ModalSheetHeader>
      <ModalSheetContent>
        <ModalSheetContentBlock>
          <ModalSheetContentTitle>
            Uploading new version to {vm.document.title}
          </ModalSheetContentTitle>
          <ModalSheetContentDescription>
            By uploading a new version of this document you’ll be replacing the
            old file. Don't worry, you can always revert back.
          </ModalSheetContentDescription>
        </ModalSheetContentBlock>
        <ModalSheetContentBlock>
          <Form onSubmit={vm.uploadVersion}>
            <input type="submit" hidden disabled={vm.uploadVersion.pending} />
            <FormRow>
              <FormGroup label="Selected File">
                <TextInput disabled value={vm.file.name} />
              </FormGroup>
            </FormRow>
            <br />
            <FormRow>
              <TextInputContainer
                label="Version Message"
                disabled={vm.uploadVersion.pending}
                placeholder={`Version ${vm.versionsCount + 1}`}
                model={vm}
                field="message"
                error={vm.validation.getError('message')}
              />
            </FormRow>
          </Form>
        </ModalSheetContentBlock>
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button onClick={vm.cancel}>Cancel</Button>
          <Button
            disabled={!vm.validation.isValid}
            onClick={vm.uploadVersion}
            loading={vm.uploadVersion.pending}
            type="primary"
          >
            Upload
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})

interface RevertVersionModalProps {
  vm: RevertVersionModalViewModel
}
export const RevertVersionModal = observer(function RevertVersionModal({
  vm,
}: RevertVersionModalProps) {
  return (
    <ModalSheet showing={vm.showing} onRequestClose={vm.cancel}>
      <ModalSheetHeader withCloseButton></ModalSheetHeader>
      <ModalSheetContent>
        <ModalSheetContentBlock>
          <ModalSheetContentTitle>
            Revert document version for {vm.document.title}
          </ModalSheetContentTitle>
          <ModalSheetContentDescription>
            Reverting creates a new version with the contents of the old one.
          </ModalSheetContentDescription>
        </ModalSheetContentBlock>
        <ModalSheetContentBlock>
          <Form onSubmit={vm.revertVersion}>
            <input type="submit" hidden disabled={vm.revertVersion.pending} />
            <FormRow>
              <FormGroup label="Version File">
                <TextInput disabled value={vm.targetVersionFile.fileName} />
              </FormGroup>
            </FormRow>
            <br />
            <FormRow>
              <FormGroup label="Originally Uploaded">
                <TextInput
                  disabled
                  value={docVersionDateFormatter.format(
                    vm.targetVersionFile.dateCreated,
                  )}
                />
              </FormGroup>
            </FormRow>
            <br />
            <FormRow>
              <TextInputContainer
                label="Reverting Message"
                disabled={vm.revertVersion.pending}
                placeholder={`Revert to version ${vm.targetVersion.message}`}
                model={vm}
                field="message"
                error={vm.validation.getError('message')}
              />
            </FormRow>
          </Form>
        </ModalSheetContentBlock>
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button onClick={vm.cancel}>Cancel</Button>
          <Button
            onClick={vm.revertVersion}
            loading={vm.revertVersion.pending}
            disabled={!vm.validation.isValid}
            type="primary"
          >
            Revert
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})
